import pluralize from "pluralize"
import _ from "lodash"
import {
  AutocompleteSearchFieldType,
  ConfigurationsType,
  MenuSearchFieldType,
  NumberSearchFieldType,
  PossibleValuesManagement,
  TextSearchFieldType,
} from "../types/configurations"
import { ShapeType } from "../types/shape"
import {
  getActiveConstraintsByFieldName,
  getAvailableMissionCodes,
  getInternalSearchFieldsConfigurationsBy,
  getSearchFieldConfigurationsBy,
  isApplicableFieldFromConfigurations, isInvalidationOrReprocessing,
  isMandatoryFieldFromConfigurations,
  isMultiSelectionFieldFromConfigurations,
  isProductionSearchField,
} from "./configurationHelper"
import { areCorrectDates } from "./dateHelper"
import { javascriptType } from "./utilsHelper"
import { getCoordinatesFromSelectedArea } from "./shapeHelper"
import translations from "../translations"
import {AppParametersType} from "../types/app";

export const selectCustomStyles = {
  control: (provided) => ({
    ...provided,
    border: "none",
    backgroundColor: "#fff",
    padding: 0,
    minHeight: "20px",
    height: "32px",
    alignItems: "flex-start",
    color: "black",
  }),
  singleValue: (provided, state) => ({
    ...provided,
    fontWeight: "600",
    padding: "0px",
    color: "black",
  }),
  indicatorSeparator: (provided, state) => ({
    ...provided,
    marginTop: "8px",
    maxHeight: "18px",
    opacity: state.isDisabled ? 0 : 1,
  }),
  dropdownIndicator: (provided, state) => ({
    ...provided,
    opacity: state.isDisabled ? 0 : 1,
  }),
}

export function labelField(
  configurations: ConfigurationsType,
  appParameters: AppParametersType,
  scopeFields: string,
  fieldConfigurations,
  formValues,
  selectedArea: ShapeType,
  language,
  bypassMandatoryLabel = false
) {
  return isMandatoryFieldFromConfigurations(
    configurations,
    appParameters,
    scopeFields,
    fieldConfigurations.name,
    formValues,
    selectedArea
  ) && !bypassMandatoryLabel
    ? `*${translations(fieldConfigurations.description, language)}`
    : translations(fieldConfigurations.description, language)
}

export const pathValueBy = (selectedArea, formValues) => {
  return getPathStringBy(selectedArea) || (formValues && formValues.path) || ""
}

export const getPathStringBy = (selectedArea) => {
  const areaCoords = getCoordinatesFromSelectedArea(selectedArea)
  return areaCoords ? areaCoords.map((e) => `${e[0]} - ${e[1]}`).join(", ") : ""
}

export const validateValuesFromConfiguration = (
  mainInputConfigurations,
  configurations: ConfigurationsType,
  appParameters: AppParametersType,
  scopeFields: string,
  selectedArea: ShapeType,
  language: string | undefined,
  searchByProducts = false
) => {
  const toReturn = (formValues) => {
    let errors = {}

    const values: any = extractFinalValues(
      mainInputConfigurations,
      configurations,
      appParameters,
      scopeFields,
      selectedArea,
      formValues
    )

    if (searchByProducts) {
      if (formValues.missions.length > 0 && formValues.product_id) {
        return {}
      }
    }

    for (const fieldName of Object.keys(mainInputConfigurations)) {
      if ({}.hasOwnProperty.call(values, fieldName)) {
        // if the key is a mainInputConfigurations
        errors = {
          ...errors,
          ...validateInputs(
            mainInputConfigurations[fieldName],
            values,
            language
          ),
        }
      }
    }

    getInternalSearchFieldsConfigurationsBy(
      configurations,
      appParameters,
      scopeFields,
      formValues
    ).forEach((searchField) => {
      errors = {
        ...errors,
        ...validateInputs(
          {
            ...searchField,
            required: isMandatoryFieldFromConfigurations(
              configurations,
              appParameters,
              scopeFields,
              searchField.name,
              values,
              selectedArea
            ),
          },
          values,
          language
        ),
      }
    })

    return errors
  }

  return toReturn
}

const validateInputValue = (fieldConfig: any, value: any) => {
  const { name: fieldName, description: fieldDescription } = fieldConfig

  const errors: any = {}

  if ("regex" in fieldConfig && !value.match(new RegExp(fieldConfig.regex))) {
    errors[fieldName] = `${fieldDescription} is not valid`
  }

  if ("minValue" in fieldConfig && fieldConfig.minValue > value) {
    errors[
      fieldName
    ] = `${fieldDescription} must be greater than ${fieldConfig.minValue}`
  }

  if ("maxValue" in fieldConfig && fieldConfig.maxValue < value) {
    errors[
      fieldName
    ] = `${fieldDescription} must be less than ${fieldConfig.maxValue}`
  }

  if ("minLength" in fieldConfig && value.length < fieldConfig.minLength) {
    errors[fieldName] = `${fieldDescription} must have at least ${pluralize(
      "item",
      fieldConfig.minLength,
      true
    )}`
  }

  if ("maxLength" in fieldConfig && value.length > fieldConfig.maxLength) {
    errors[fieldName] = `${fieldDescription} must have at least ${pluralize(
      "item",
      fieldConfig.maxLength,
      true
    )}`
  }

  return errors
}

const validateInputs = (fieldConfig, values, language) => {
  let errors: any = {}
  const { name: fieldName, description: fieldDescription } = fieldConfig
  const value = values[fieldName]

  if (hasBeenFilled(value)) {
    if (Array.isArray(value)) {
      errors = value.reduce((acc, v) => {
        const newErrors = {
          ...acc,
          ...validateInputValue(fieldConfig, v),
        }
        if (!hasBeenFilled(v)) {
          newErrors[
            fieldName
          ] = `${fieldDescription} must have min and max values`
        }
        return newErrors
      }, errors)
      // eslint-disable-next-line valid-typeof
    } else if (
      "type" in fieldConfig &&
      typeof value !== javascriptType(fieldConfig.type)
    ) {
      errors[fieldName] = `${fieldDescription} must be a ${fieldConfig.type}`
    } else if (
      ["date1", "date2"].includes(fieldName) &&
      areCorrectDates(values.date1, values.date2)
    ) {
      errors[fieldName] = "Set the correct range"
    } else {
      errors = validateInputValue(fieldConfig, value)
    }
  } else if (!isProductionSearchField(fieldConfig) && "required" in fieldConfig && fieldConfig.required) {
    errors[fieldName] = `${translations(
      fieldDescription,
      language
    )} ${translations("isRequired", language)}`
  }

  return errors
}

export const extractFinalValues = (
  mainInputConfigurations,
  configurations: ConfigurationsType,
  appParameters: AppParametersType,
  scopeFields: string,
  selectedArea: ShapeType,
  formValues
) => {
  const finaleValues = {}

  for (const fieldName of Object.keys(mainInputConfigurations)) {
    if ({}.hasOwnProperty.call(formValues, fieldName)) {
      switch (fieldName) {
        case "path":
          finaleValues[fieldName] = pathValueBy(selectedArea, formValues)
          break
        default:
          finaleValues[fieldName] = formValues[fieldName]
      }
    }
  }

  getInternalSearchFieldsConfigurationsBy(
    configurations,
    appParameters,
    scopeFields,
    formValues
  ).forEach((searchField) => {
    finaleValues[searchField.name] = extractFinalValue(
      configurations,
      appParameters,
      scopeFields,
      searchField.name,
      selectedArea,
      formValues
    )
  })

  return finaleValues
}

export const extractFinalValue = (
  configurations: ConfigurationsType,
  appParameters: AppParametersType,
  scopeFields: string,
  fieldName: string,
  selectedArea: ShapeType,
  formValues
) => {
  let finalValue
  const searchFieldsConfigurations = getInternalSearchFieldsConfigurationsBy(
    configurations,
    appParameters,
    scopeFields,
    formValues
  )
  const searchFieldConfig = searchFieldsConfigurations.find(
    (searchField) => searchField.name === fieldName
  )
  if (searchFieldConfig) {
    if ("possibleValuesManagement" in searchFieldConfig) {
      switch (searchFieldConfig.possibleValuesManagement) {
        case PossibleValuesManagement.MENU:
          finalValue = getValueObjectBy(
            configurations,
            appParameters,
            scopeFields,
            searchFieldConfig.name,
            formValues,
            selectedArea
          ).value
          break
        case PossibleValuesManagement.AUTOCOMPLETE:
        case PossibleValuesManagement.RANGE:
          finalValue = formValues[fieldName]
          break
        default:
          console.error("Input is not being handled")
      }
    } else {
      finalValue = formValues[fieldName]
    }
  }
  return finalValue
}

export const getInitialValueFromConfiguration = (
  mainInputConfigurations,
  configurations: ConfigurationsType,
  appParameters: AppParametersType,
  scopeFields: string,
  selectedArea: ShapeType,
  defaultValues: any = {}
) => {
  const initValues = {
    ...defaultValues,
  }

  for (const key of Object.keys(mainInputConfigurations)) {
    if ({}.hasOwnProperty.call(mainInputConfigurations, key)) {
      const conf = mainInputConfigurations[key]
      initValues[key] = conf.default
    }
  }

  // if there is one mission available, select it as default
  if ("missions" in initValues && "missions" in configurations) {
    const missionCodes = getAvailableMissionCodes(configurations, initValues)
    if (missionCodes.length === 1) {
      initValues.missions = missionCodes
    }
  }

  const internalSearchFieldsConfigurations =
    getInternalSearchFieldsConfigurationsBy(
      configurations,
      appParameters,
      scopeFields,
      initValues
    )
  if (
    internalSearchFieldsConfigurations &&
    internalSearchFieldsConfigurations.length > 0
  ) {
    const allNames = internalSearchFieldsConfigurations.map((isfc) => isfc.name)
    let isOver = true
    do {
      isOver = true
      const newValuesFieldNames = Object.keys(initValues)
      allNames
        .filter((fieldName) => !newValuesFieldNames.includes(fieldName))
        // eslint-disable-next-line
        .forEach((fieldName) => {
          const { value } = getValueObjectBy(
            configurations,
            appParameters,
            scopeFields,
            fieldName,
            initValues,
            selectedArea
          )
          if (value) {
            initValues[fieldName] = value
            isOver = false
          }
        })
    } while (!isOver)
  } else if ("missions" in configurations) {
    configurations.missions.forEach((mission) => {
      mission.fields[scopeFields]
        .filter((field) => field.description !== "EXTERNAL")
        .forEach((field) => {
          initValues[field.name] = null
        })
    })
  } else if ("search" in configurations.fields) {
    configurations.fields.search
      .filter((field) => field.description !== "EXTERNAL")
      .forEach((field) => {
        initValues[field.name] = null
      })
  }

  return initValues
}

export const onlyUnique = (value, index, self) => self.indexOf(value) === index

export function getPossibleValuesFromConfigurations(
  configurations: ConfigurationsType,
  appParameters: AppParametersType,
  scopeFields: string,
  fieldName: string,
  formValues,
  selectedArea: ShapeType,
  isProductionField = false
): string[] {
  const possibleValues: string[][] = []
  const constraints = getActiveConstraintsByFieldName(
    configurations,
    appParameters,
    scopeFields,
    formValues,
    selectedArea,
    fieldName,
    isProductionField
  );
  constraints.forEach((constraint) => {
    if ("possibleValues" in constraint) {
      possibleValues.push(constraint.possibleValues)
    }
  });

  // filter of all possible values by unique value
  return possibleValues
    .reduce((a, b) => a.concat(b), [])
    .filter((e) => possibleValues.every((a) => a.includes(e)))
    .filter(onlyUnique)
}

export const getValueObjectBy = (
  configurations: ConfigurationsType,
  appParameters: AppParametersType,
  scopeFields: string,
  fieldName: string,
  formValues,
  selectedArea: ShapeType,
  isProductionField = false
) => {
  const isApplicable = isApplicableFieldFromConfigurations(
    configurations,
    appParameters,
    scopeFields,
    fieldName,
    formValues,
    selectedArea
  )
  let possibleValues: any = []
  let value
  // if the input is disable, don't use the value
  if (isApplicable) {
    const isMultipleSelectionAvailable =
      isMultiSelectionFieldFromConfigurations(
        configurations,
        appParameters,
        scopeFields,
        fieldName,
        formValues,
        selectedArea
      );

    possibleValues = getPossibleValuesFromConfigurations(
      configurations,
      appParameters,
      scopeFields,
      fieldName,
      formValues,
      selectedArea,
      isProductionField
    )
    const fieldValue = formValues[fieldName]
    if (hasBeenFilled(fieldValue)) {
      if (isMultipleSelectionAvailable) {
        value = _.intersection(possibleValues, fieldValue)
      } else if (possibleValues.includes(fieldValue)) {
        value = fieldValue
      }
    }

    if (!hasBeenFilled(value)) {
      if (isMultipleSelectionAvailable) {
        value = possibleValues.length === 1 ? [possibleValues[0]] : []
      } else {
        value = possibleValues.length === 1 ? possibleValues[0] : ""
      }
    }
  }

  return {
    possibleValues,
    value,
  }
}

export const getPlaceholderForNumberFieldConfiguration = (
  fieldConfigurations: NumberSearchFieldType
): string => {
  let { placeholder } = fieldConfigurations
  if (!placeholder) {
    placeholder =
      "minValue" in fieldConfigurations
        ? `[${fieldConfigurations.minValue}`
        : "(-infinity"
    placeholder =
      "maxValue" in fieldConfigurations
        ? `${placeholder}, ${fieldConfigurations.maxValue}]`
        : `${placeholder}, +infinity)`
  }
  return placeholder
}

export const getPlaceholderForSelectField = (language: string): string => {
  return translations("selectSearchFilters", language)
}

export const getPlaceholderForMenuFieldConfiguration = (
  fieldConfigurations: MenuSearchFieldType,
  language: string
): string => {
  return (
    (fieldConfigurations &&
      translations(fieldConfigurations.placeholder, language)) ||
    getPlaceholderForSelectField(language)
  )
}

export const getPlaceholderForAutocompleteFieldConfiguration = (
  fieldConfigurations: AutocompleteSearchFieldType,
  language: string
): string => {
  return (
    (fieldConfigurations &&
      translations(fieldConfigurations.placeholder, language)) ||
    "Digit..."
  )
}

export const getPlaceholderForTextFieldConfiguration = (
  fieldConfigurations: TextSearchFieldType,
  language: string
): string => {
  return (
    (fieldConfigurations &&
      translations(fieldConfigurations.placeholder, language)) ||
    "Digit..."
  )
}

export const extractFiltersBy = (
  configurations,
  appParameters,
  mainInputConfigurations,
  scopeFields,
  formValues
) => {
  return Object.keys(formValues).reduce((acc, fieldName) => {
    const value = formValues[fieldName] === "All" ? "" : formValues[fieldName]

    if (value !== undefined && value !== null && value !== "") {
      if (Object.keys(mainInputConfigurations).includes(fieldName)) {
        const externalFieldName = fieldName.toUpperCase()
        const field = getSearchFieldConfigurationsBy(
          configurations,
          appParameters,
          scopeFields,
          externalFieldName,
          formValues
        )
        if (field) {
          acc[externalFieldName] = {
            value: value.toString().toUpperCase(),
            field,
          }
        }
      } else {
        acc[fieldName] = {
          value,
          field: getSearchFieldConfigurationsBy(
            configurations,
            appParameters,
            scopeFields,
            fieldName,
            formValues
          ),
        }
      }
    }

    return acc
  }, {})
}

export const allRequiredFieldsHaveBeenFilled = (
  mainInputConfigurations,
  configurations: ConfigurationsType,
  scopeFields: string,
  formValues,
  selectedArea: ShapeType,
  searchByProducts = false,
  appParameters,
): boolean => {
  let haveBeenFilled = true;

  // 1- selectedArea
  if (!searchByProducts && !isInvalidationOrReprocessing(configurations, appParameters)) {
    haveBeenFilled = !!selectedArea;
  }

  // 2- campi della form
  if (searchByProducts) {
    if (formValues.missions.length > 0 && formValues.product_id) return true
  }
  const values: any = extractFinalValues(
    mainInputConfigurations,
    configurations,
    appParameters,
    scopeFields,
    selectedArea,
    formValues
  )
  for (const fieldName of Object.keys(mainInputConfigurations)) {
    if ({}.hasOwnProperty.call(values, fieldName)) {
      // if the key is a mainInputConfigurations
      haveBeenFilled =
        haveBeenFilled &&
        (!mainInputConfigurations[fieldName].required ||
          hasBeenFilled(values[fieldName]))
    }
  }

  getInternalSearchFieldsConfigurationsBy(configurations, appParameters, scopeFields, {
    ...formValues,
    ...values,
  }).forEach((searchField) => {
    haveBeenFilled =
      haveBeenFilled &&
      (isProductionSearchField(searchField) ||
        !isMandatoryFieldFromConfigurations(
          configurations,
          appParameters,
          scopeFields,
          searchField.name,
          { ...formValues, ...values },
          selectedArea
        ) ||
        hasBeenFilled(values[searchField.name]))
  });

  return haveBeenFilled
}

export const hasBeenFilled = (value): boolean => {
  const type = typeof value

  return type === "object" && value !== null
    ? Object.keys(value).length > 0
    : value !== null && value !== undefined && value !== ""
}
